import cx from 'classnames'
import { Form, Formik, FormikProps } from 'formik'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import * as yup from 'yup'

import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { Button } from 'blaze-ui/Button'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { authFromResponse } from 'utils'
import { reportError } from 'utils/error'
import Shared from '../../Shared.module.scss'
import PasswordFields from '../PasswordFields/PasswordFields'
import { ArrowRight } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Spinner } from 'common/Spinner/Spinner'
import { useIsBlaze } from 'hooks/useIsBlaze'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import { SIGNED_UP_EVENT, trackEvent } from 'utils/trackingUtils'
import { RegistrationType, splitUserName, trackCreateAccountComplete, trackCreateAccountFailed } from '../../utils'
import { Testimonials } from '../Blaze/Testimonials/Testimonials'
import ProgressBar from '../ProgressBar/ProgressBar'
import Styles from './CreateAccount.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import ImageAssembly from '../Blaze/Testimonials/CartoonTestimonials/ImageAssembly'
import { GhostButton } from 'components/Buttons/GhostButton'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'

type Props = {
  email: string
  token: string
  transitionPage: (page: string) => void
  redirectTo: string
  workspaceInviteToken: string
  totalSteps: number
  emailVerificationCode?: string
  trackRegistrationStarted: () => void
}

type FormValueType = {
  name: string
  password: string
}

const initialValues = {
  name: '',
  password: '',
}

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  password: yup
    .string()
    .required('isRequired')
    .min(8, 'isNotLongEnough')
    .matches(/\d+/, 'noNumber')
    .matches(/[A-Z]+/, 'noCapital'),
})

const ERROR_FIELD_LABELS: { [key: string]: string | number } = {
  name: 'Name',
  password: 'Password',
}

const CreateAccount = (props: Props) => {
  const {
    email,
    token,
    transitionPage,
    redirectTo,
    workspaceInviteToken,
    emailVerificationCode,
    totalSteps,
    trackRegistrationStarted,
  } = props
  const [creationError, setCreationError] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [submittedWithErrors, setSubmittedWithErrors] = useState(false)
  const [nameSubmitted, setNameSubmitted] = useState(false)
  const [passwordSubmitted, setPasswordSubmitted] = useState(false)
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const queryClient = useQueryClient()
  const isBlaze = useIsBlaze()

  const formikRef = useRef<FormikProps<FormValueType>>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  const { onLoginOrSignUpSuccess: onSignUpSuccess } = useLoginOrSignUp()

  const createAccount = async ({ name, password }: { name: string; password: string }) => {
    const { firstName, lastName } = splitUserName(name)
    if (!firstName) {
      setCreationError(true)
      return
    }

    setIsProcessing(true)
    const res = await UserClient.registerUser(
      firstName,
      lastName,
      password,
      token,
      emailVerificationCode,
      email,
      workspaceInviteToken,
      redirectTo,
    )

    if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.success) {
      setCreationError(true)
      setIsProcessing(false)
      trackCreateAccountFailed(res.data.error)
      reportError(res.data.error, { metaData: 'Create account failed' })
      return
    }

    const auth = authFromResponse(res.data.user, res.headers)
    const nextPage = await onSignUpSuccess(auth, RegistrationType.EmailAndPassword, redirectTo, workspaceInviteToken)

    queryClient.invalidateQueries('featureFlags')
    setIsProcessing(false)
    trackCreateAccountComplete()
    trackEvent(SIGNED_UP_EVENT, undefined, {
      email: auth.email,
      almanac_user_id: auth.id,
    })

    if (nextPage) transitionPage(nextPage)
  }

  const errorMessage = (errors: { name?: string; password?: string }) => {
    const errorFields = Object.keys(errors)
      .map((key) => ERROR_FIELD_LABELS[key])
      .join(', ')

    return `Please check the ${errorFields || ''} ${Object.keys(errors).length > 1 ? ' fields' : ' field'} above`
  }

  const handleSubmitName = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault()
    setNameSubmitted(true)
    passwordInputRef?.current?.select()
  }

  const handleNameKeyPressEnter = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'Tab') {
      e.preventDefault()
      handleSubmitName(e)
    }
  }

  const handlePasswordPressEnter = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter') {
      setPasswordSubmitted(true)
    }
  }

  useEffect(() => {
    if (!!window.heap) {
      trackRegistrationStarted()
      return
    }

    setTimeout(() => {
      trackRegistrationStarted()
    }, 800)
  }, [trackRegistrationStarted])

  const onButtonPress = () => {
    return transitionPage('GO_BACK')
  }

  return (
    <div data-testid="createAccount" className={cx(Shared.content, { [Shared.isBlaze]: isBlaze })}>
      <div className={Shared.leftPanel}>
        <div className={Shared.header}>
          <GhostButton className={cx(Shared.backButton)} aria-label="Back" onClick={onButtonPress}>
            <LeftArrowIcon />
          </GhostButton>
          <ProgressBar totalSteps={totalSteps} currentStep={2} />
        </div>
        <div className={Styles.leftContent}>
          <div className={Styles.gapHeader}>
            <Heading level={isMobile ? 3 : 1}>{isMobile ? "You're almost signed up!" : 'Welcome to Blaze!'}</Heading>
            <div className={Styles.gapSubtitle}>
              <Text variant="primary" color="var(--dark-60)">
                Tell us a bit about yourself.
              </Text>
            </div>
          </div>
          {creationError && (
            <div className={Shared.errorContainer}>
              <p className={Shared.creationError}>
                An error occurred, or there may already be an account associated with this email.{' '}
                <button
                  type="button"
                  onClick={() => {
                    setCreationError(false)
                    formikRef.current?.resetForm()
                  }}
                >
                  Try again.
                </button>
              </p>
            </div>
          )}
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={createAccount}
            validationSchema={validationSchema}
            validateOnChange
            validateOnMount
          >
            {({ values, errors, handleChange }) => {
              const { password } = values
              const handlePasswordSubmit = () => {
                Object.keys(errors).length ? setSubmittedWithErrors(true) : setSubmittedWithErrors(false)
              }

              return (
                <Form className={Styles.form} autoComplete="off">
                  <div>
                    <Text variant="label">
                      Full Name<p className={Styles.warning}>*</p>
                    </Text>
                  </div>
                  <Input
                    id="name"
                    name="name"
                    size="xl"
                    placeholder="John Doe"
                    isValid={!nameSubmitted || !errors.name}
                    autoFocus
                    autoComplete="name"
                    onChange={handleChange}
                    onKeyDown={handleNameKeyPressEnter}
                    data-heap-id="enter-full-name"
                  />
                  <div className={Styles.gapForm}>
                    <PasswordFields
                      password={password}
                      passwordLabel="Password"
                      errors={errors}
                      handleChange={handleChange}
                      passwordSubmitted={passwordSubmitted}
                      handleSubmitPassword={handlePasswordPressEnter}
                      submittedWithErrors={submittedWithErrors}
                      isProcessing={isProcessing}
                      passwordInputRef={passwordInputRef}
                      passwordPlaceholder="Enter Password"
                    />
                    {!!Object.keys(errors).length && passwordSubmitted && (
                      <FormErrorMessage>{errorMessage(errors)}</FormErrorMessage>
                    )}
                  </div>
                  <div className={Styles.buttonContainer}>
                    <Button
                      data-test-id="submit-password"
                      type="submit"
                      onPress={handlePasswordSubmit}
                      variant="primary"
                      endIcon={ArrowRight}
                      fullWidth={isMobile}
                      id="submit-password"
                      size="lg"
                      isDisabled={isProcessing || !!errors?.password?.length || !!errors?.name?.length}
                    >
                      <div className={Styles.buttonContent}>
                        {isProcessing && <Spinner className={Shared.spinner} />}
                        <span>{isProcessing ? 'Creating account...' : `Continue`}</span>
                      </div>
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
          {isMobile && <Testimonials currentPage="createAccount" inline newFormat />}
        </div>
      </div>
      {!isTabletPortrait && !isMobile && <ImageAssembly offsetTop={195} useSmallerBubble page={'createAccount'} />}
    </div>
  )
}

export default CreateAccount
